import React, { useEffect, useRef } from "react";
import folderIcon from "@/images/icons/folder-open-outline.svg";
import downloadIcon from "@/images/icons/download-icon.svg";
import closeIcon from "@/images/icons/close-modal-button.svg";
import "./ResourceListPopout.scss";
import { ResourceBody } from "./ResourceBody";
import { Popover } from 'bootstrap';
import { AmiraResources } from "@/models/AmiraResources.interface";
import ResourcesImage from "@/images/icons/teacher-icon.svg";
import { Placeholder } from "@/components/Global";
interface ResourceListPopoutProps {
  resources: AmiraResources[];
  placement: "top" | "right" | "bottom" | "left";
  lesson: string;
  areResourcesLoading: boolean;
  handleOpenDetails: () => void;
}

export const ResourceListPopout = ({ resources, placement, lesson, handleOpenDetails, areResourcesLoading }: ResourceListPopoutProps) => {
  const popoverRef = useRef<HTMLButtonElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const handleDownloadAll = async () => {
    if (!resources) return;

    for (const resource of resources) {
      try {
        const response = await fetch(resource.url, { mode: "cors" });
        if (!response.ok) throw new Error(`Failed to fetch ${resource.url}`);
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = resource.title || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error(`Error downloading ${resource.title}:`, error);
      }
    }
  };

  useEffect(() => {
    if (areResourcesLoading) return;
    const popoverTriggerEl = popoverRef.current;
    const popoverContentEl = popoverContentRef.current;

    if (popoverTriggerEl && popoverContentEl) {
      const popoverInstance = new Popover(popoverTriggerEl, {
        html: true,
        sanitize: false,
        trigger: "click",
        content: popoverContentEl || '',
        placement: placement,
      });

      const handleButtonClick = (event: Event) => {
        if ((event.target as HTMLElement).id === "popover-open-details-button") {
          handleOpenDetails();
        } else if ((event.target as HTMLElement).id === "popover-close-icon") {
          popoverInstance.hide();
        } else if ((event.target as HTMLElement).id === "popover-download-all-button") {
          handleDownloadAll();
        }
      };

      const handleDocumentClick = (event: MouseEvent) => {
        const target = event.target as Node;
        const popoverEl = document.querySelector('.custom-popover');

        if (
          popoverEl &&
          popoverTriggerEl &&
          !popoverTriggerEl.contains(target) &&
          !popoverEl.contains(target)
        ) {
          popoverInstance.hide();
        }
      };

      popoverTriggerEl.addEventListener('shown.bs.popover', () => {
        document.addEventListener('click', handleDocumentClick);
      });

      popoverTriggerEl.addEventListener('hidden.bs.popover', () => {
        document.removeEventListener('click', handleDocumentClick);
      });

      popoverContentEl.addEventListener("click", handleButtonClick);

      return () => {
        popoverContentEl.removeEventListener("click", handleButtonClick);
        document.removeEventListener('click', handleDocumentClick);
        popoverTriggerEl.removeEventListener('shown.bs.popover', () => {});
        popoverTriggerEl.removeEventListener('hidden.bs.popover', () => {});
        popoverInstance.dispose();
      };
    }
  }, [areResourcesLoading]);

  return (
    <section>
      {areResourcesLoading ? <Placeholder colWidth={12} heightInRem={3} /> : (
        <>
        <button
          ref={popoverRef}
          className="resource-popout-button"
          data-bs-toggle="popover"
          data-bs-placement="right"
          aria-label="Resources"
          data-bs-custom-class="custom-popover"
        >
          <div className="resource-popout-button-icon d-flex align-items-center align-content-center">
            <img
              src={ResourcesImage}
              alt="Resource Folder icon"
              className="resource-popout-button-icon-image"
            />
          </div>
          <div className="resource-popout-button-text d-flex align-items-center">Resources</div>
        </button>
        <section className="d-none">
          <div ref={popoverContentRef} className="resource-modal d-flex flex-column">
            <div className="lesson-block-header d-flex align-items-center">
              <div className="lesson-header-text-container d-flex align-items-center">
                <div className="lesson-header-icon d-flex align-items-center">
                  <img src={ResourcesImage} alt="Folder icon" />
                </div>
                <div className="lesson-header-text">{lesson}</div>
              </div>
              <button className="close-icon" id="popover-close-icon" aria-label="Close Popover">
                <img src={closeIcon} alt="Close icon" className="close-icon-image pe-none" />
              </button>
            </div>
            <ResourceBody
              resources={resources}
              handleOpenDetails={handleOpenDetails}
            />
            <div className="resource-footer d-flex align-items-center justify-content-center">
              <button
                className="bulk-download-button d-flex align-items-center"
                onClick={handleDownloadAll}
                type="button"
                id="popover-download-all-button"
                aria-label="Download all resources"
              >
                <div className="bulk-download-button-text pe-none">
                  Download all ({resources?.length})
                </div>
                <div className="bulk-download-button-icon pe-none d-flex align-items-center justify-content-center">
                  <img
                    src={downloadIcon}
                    alt="Download icon"
                    className="bulk-download-button-icon-image pe-none"
                  />
                </div>
              </button>
            </div>
          </div>
          </section>
        </>
      )}
    </section>
  );
};
